import {
  Typography,
  IconButton,
  Box,
  Button,
  InputAdornment,
  Stack,
  Collapse
} from "@mui/material";
import { Add as AddIcon, CheckCircle, Delete as DeleteIcon, ErrorOutline, Sync, KeyboardArrowRight } from "@mui/icons-material";
import { useFieldArray, FormProvider } from "react-hook-form";
import { TextFieldElement } from "react-hook-form-mui";
import { gstSettingSchema, IGstSettingsFormValues } from "@/utils/zod/generalSettingsFormValidation";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "@refinedev/react-hook-form";
import { AutoSaveIndicator } from "@refinedev/mui";
import { useState } from "react";

// Define a wrapper type for the form structure
interface GstSettingsForm {
  gst_settings: IGstSettingsFormValues[];
}

const GstSettingsSection = ({ values }: { values: { gst_settings: IGstSettingsFormValues[] } }) => {
  const [isExpanded, setIsExpanded] = useState(true);

  const methods = useForm<GstSettingsForm>({
    refineCoreProps: {
      action: "edit",
      resource: "general-settings",
      id: "gst_settings",
      queryOptions: {
        enabled: false,
      },
      autoSave: {
        enabled: true,
        debounce: 2000,
        onFinish: (values) => {
          let isValid = true;
          values.gst_settings.map((setting: IGstSettingsFormValues, index: number) => {
            if (setting.gstRate > 100 || setting.gstRate < 0) {
              methods.setError(`gst_settings.${index}.gstRate`, {
                type: "manual",
                message: "GST Rate must be between 0 and 100"
              });
              isValid = false;
              return;
            } else {
              methods.clearErrors(`gst_settings.${index}.gstRate`); // Clears error when valid
            }

            if (setting.gstRate < values.gst_settings[index - 1]?.gstRate || setting.gstRate > values.gst_settings[index + 1]?.gstRate) {
              methods.setError(`gst_settings.${index}.gstRate`, {
                type: "manual",
                message: "GST Rate must be greater than the previous GST Rate and less than the next GST Rate"
              });
              isValid = false;
              return;
            } else {
              methods.clearErrors(`gst_settings.${index}.gstRate`); // Clears error when valid
            }

            if (setting.priceUptoInclusive && setting.priceUptoInclusive < 0) {
              methods.setError(`gst_settings.${index}.priceUptoInclusive`, {
                type: "manual",
                message: "Price Up To Inclusive must be greater than 0"
              });
              isValid = false;
              return;
            } else {
              methods.clearErrors(`gst_settings.${index}.priceUptoInclusive`); // Clears error when valid
            }

            if (setting.priceUptoInclusive && (setting.priceUptoInclusive < values.gst_settings[index - 1]?.priceUptoInclusive || values.gst_settings[index + 1]?.priceUptoInclusive && setting.priceUptoInclusive > values.gst_settings[index + 1]?.priceUptoInclusive)) {
              methods.setError(`gst_settings.${index}.priceUptoInclusive`, {
                type: "manual",
                message: "Price Up To Inclusive must be greater than the previous Price Up To Inclusive"
              });
              isValid = false;
              return;
            } else {
              methods.clearErrors(`gst_settings.${index}.priceUptoInclusive`); // Clears error when valid
            }

            return setting;
          });

          if (!isValid) {
            return Promise.reject(new Error("Invalid GST rates, skipping auto-save."));
          }
          return values.gst_settings;
        },
      },
    },
    resolver: zodResolver(
      gstSettingSchema.extend({
        gst_settings: gstSettingSchema.array()
      })
    ),
    defaultValues: {
      gst_settings: values.gst_settings
    },
    values: values
  });

  const { control, formState: { errors }, refineCore: { autoSaveProps, onFinishAutoSave }, getValues, watch } = methods;

  const fieldArray = useFieldArray({
    control,
    name: "gst_settings",
  });

  const handleRemoveGstSetting = (index: number) => {
    fieldArray.remove(index);
    onFinishAutoSave(getValues().gst_settings);
  };

  const handleAddGstSetting = () => {
    const settings = watch("gst_settings");
    const lastSetting = settings[settings.length - 1];
    if (!lastSetting.gstRate) {
      methods.setError(`gst_settings.${settings.length - 1}.gstRate`, {
        type: "manual",
        message: "Please fill this field before adding another GST Rate"
      });
      return;
    }
    if (!lastSetting.priceUptoInclusive) {
      methods.setError(`gst_settings.${settings.length - 1}.priceUptoInclusive`, {
        type: "manual",
        message: "Please fill this field before adding another phone number"
      });
      return;
    }
    methods.clearErrors(`gst_settings.${settings.length - 1}.gstRate`);
    fieldArray.append({ gstRate: 0, priceUptoInclusive: null });
  };

  return (
    <FormProvider {...methods}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <IconButton onClick={() => setIsExpanded(!isExpanded)}>
            <KeyboardArrowRight
              sx={{
                color: 'primary.main',
                fontSize: '2rem',
                transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                transition: 'transform 0.3s'
              }}
            />
          </IconButton>
          <Typography variant="h6">
            GST Settings
          </Typography>
          {isExpanded && <AutoSaveIndicator {...autoSaveProps} elements={{
            success: <Typography variant="body1" sx={{ color: 'success.main', display: 'flex', alignItems: 'center', gap: 1 }}><CheckCircle /> Saved</Typography>,
            error: <Typography variant="body1" sx={{ color: 'error.main', display: 'flex', alignItems: 'center', gap: 1 }}><ErrorOutline /> Error</Typography>,
            loading: <Typography variant="body1" sx={{ color: 'warning.main', display: 'flex', alignItems: 'center', gap: 1 }}><Sync /> Saving...</Typography>,
            idle: <Typography variant="body1" sx={{ color: 'grey.500', display: 'flex', alignItems: 'center', gap: 1 }}>Waiting for changes ...</Typography>
          }} />}
        </Stack>
        <Button
          startIcon={<AddIcon />}
          onClick={() => handleAddGstSetting()}
          variant="text"
          sx={{
            display: isExpanded ? 'flex' : 'none',
          }}
        >
          Add GST Slab
        </Button>
      </Box>

      <Collapse in={isExpanded} sx={{ paddingLeft: '2rem' }}>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="body1" sx={{ flex: 1, textAlign: 'left', fontWeight: 'bold' }}>
            Price Up To Inclusive
          </Typography>
          <Typography variant="body1" sx={{ flex: 1, textAlign: 'left', fontWeight: 'bold' }}>
            GST Rate
          </Typography>
          <Box sx={{ width: 40 }} /> {/* Spacer for delete button alignment */}
        </Stack>

        {fieldArray.fields.map((field, index) => (
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }} key={field.id}>
            <TextFieldElement
              name={`gst_settings[${index}].priceUptoInclusive`}
              type="number"
              fullWidth
              slotProps={{
                htmlInput: {
                  min: 0,
                  placeholder: "Keep blank for no limit"
                },
                input: {
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>
                }
              }}
              helperText={errors.root?.message}
              error={!!errors.root}
              size="small"
            />
            <TextFieldElement
              name={`gst_settings[${index}].gstRate`}
              type="number"
              fullWidth
              slotProps={{
                htmlInput: {
                  min: 0,
                  max: 100
                },
                input: {
                  endAdornment: <InputAdornment position="end">%</InputAdornment>
                }
              }}
              helperText={errors.root?.message}
              error={!!errors.root}
              size="small"
            />
            <IconButton onClick={() => handleRemoveGstSetting(index)} color="error">
              <DeleteIcon />
            </IconButton>
          </Stack>
        ))}
      </Collapse>
    </FormProvider>
  );
};

export default GstSettingsSection; 